// apps/web/src/features/cargo/useCargoFilters.ts
//
// URL-canonical `/cargo` filters (#1231) with authenticated localStorage
// fallback (#2104). Hydration priority is URL > storage > defaults; `q` stays
// URL-only. Serialization/persistence helpers live in cargoFiltersStorage.ts.
//
// Logist roles get a frontend-only recent `china_ship` window until any date
// interaction persists `datesTouched`; clearing/resetting remains unbounded.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import type {
  CargoCarrierPaymentStatus,
  CargoClientPaymentStatus,
  CargoDateType,
  CargoLifecycle,
  CargoListQuery,
  CargoOverdueBucket,
} from '@magimp/shared';
import { useAuthStore } from '../../stores/auth.js';
import {
  buildPersistedCargoQuery,
  hasPersistedCargoFilterParam,
  isCargoRangeBoundActive,
  readState,
  safeReadCargoFilters,
  safeWriteCargoFilters,
  writeState,
  CARGO_SEARCH_MAX_CHARS,
  type CargoFiltersState,
} from './cargoFiltersStorage.js';

// Re-export so existing imports from `../useCargoFilters.js` keep working.
export { CARGO_SEARCH_MAX_CHARS };
export type { CargoFiltersState };

/** Local-midnight start of `YYYY-MM-DD`, as an offset ISO instant. */
function dayStartIso(day: string): string {
  return new Date(`${day}T00:00:00`).toISOString();
}

/** Local end-of-day (start of next day) of `YYYY-MM-DD`, as an offset instant. */
function dayEndIso(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
}

function firstDayOfPreviousMonthIso(now: Date): string {
  return new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0).toISOString();
}

/** Format a Date as local YYYY-MM-DD without UTC shifting. */
function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface UseCargoFiltersInput {
  /** Apply the logist default-date window when no date is explicitly chosen. */
  applyDefaultDateWindow: boolean;
}

export type CargoRangeBoundKey =
  | 'weightFrom'
  | 'weightTo'
  | 'volumeFrom'
  | 'volumeTo'
  | 'totalCostFrom'
  | 'totalCostTo';

export interface UseCargoFiltersResult extends CargoFiltersState {
  setQ: (next: string) => void;
  setLifecycle: (next: CargoLifecycle | null) => void;
  setResponsibleManagerId: (next: string) => void;
  setCarrier: (next: string | null) => void;
  setPathFormat: (next: string | null) => void;
  setClientPaymentStatus: (next: CargoClientPaymentStatus | null) => void;
  setCarrierPaymentStatus: (next: CargoCarrierPaymentStatus | null) => void;
  setDateType: (next: CargoDateType) => void;
  setDateFrom: (next: string) => void;
  setDateTo: (next: string) => void;
  setRangeBound: (key: CargoRangeBoundKey, next: string) => void;
  setOverdue: (next: CargoOverdueBucket | null) => void;
  setHasRejectedDocument: (next: boolean) => void;
  resetFilters: () => void;
  /** True while the logist default-date window is supplying the range. */
  usingDefaultDateWindow: boolean;
  /**
   * The exact local date range sent to the backend when `usingDefaultDateWindow`
   * is true; null otherwise. Used by the active-filter chip to show the real
   * window rather than a generic label (FR-5, #1887).
   */
  defaultDateWindow: { from: string; to: string } | null;
  /** Wire-filter shape ready for the list + summary queries. */
  wireFilters: Partial<CargoListQuery>;
}

/**
 * @aiPurpose URL-canonical filter ownership for `/cargo` (#1231) with a
 *   per-browser localStorage fallback (#2104). Hydrates once from URL >
 *   localStorage > defaults, exposes typed setters that rewrite the canonical
 *   params, write-throughs the last-used filter to localStorage, and builds
 *   the wire-filter envelope (widening day strings to instant boundaries and
 *   applying the logist default-date window).
 * @aiSecurity localStorage writes re-check `useAuthStore.getState().user`
 *   immediately before every put so an anonymous shared URL or a cross-tab
 *   logout race cannot reseed the persisted key after `userCacheReset` removed
 *   it.
 */
export function useCargoFilters(input: UseCargoFiltersInput): UseCargoFiltersResult {
  const { applyDefaultDateWindow } = input;
  const [searchParams, setSearchParams] = useSearchParams();

  // Hydration priority (#2104): URL persisted filters > localStorage >
  // defaults. `q` is URL-only — captured from the URL but never restored from
  // storage. `hasPersistedCargoFilterParam` ignores `q`, so a bare `?q=...`
  // does NOT make the URL authoritative over a stored payload.
  const initialRef = useRef<CargoFiltersState | null>(null);
  const hydratedFromStorageRef = useRef<boolean>(false);
  if (initialRef.current === null) {
    const urlState = readState(searchParams);
    if (hasPersistedCargoFilterParam(searchParams)) {
      initialRef.current = urlState;
    } else {
      const stored = safeReadCargoFilters();
      if (stored != null && stored.length > 0) {
        const sp = new URLSearchParams(stored);
        sp.delete('q'); // never restore a stale persisted q
        initialRef.current = { ...readState(sp), q: urlState.q };
        hydratedFromStorageRef.current = true;
      } else {
        initialRef.current = urlState;
      }
    }
  }
  const [state, setState] = useState<CargoFiltersState>(initialRef.current);

  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  // Mirror current state so a setter computes the next value without nesting
  // a `setSearchParams` call inside a `setState` updater (that would be a
  // setState-in-render of the router).
  const stateRef = useRef(state);
  stateRef.current = state;

  // Guards the searchParams→state sync effect from reacting to our OWN URL
  // write (which would be a redundant re-read at best, a loop at worst).
  const justWroteRef = useRef(false);
  // Most recent payload written to localStorage; skips redundant writes and
  // Strict Mode's double-effect run.
  const lastWrittenStorageRef = useRef<string | null>(null);

  const commit = useCallback(
    (updater: (prev: CargoFiltersState) => CargoFiltersState) => {
      const next = updater(stateRef.current);
      if (next === stateRef.current) return;
      stateRef.current = next;
      setState(next);
      const nextSearchParams = writeState(searchParamsRef.current, next);
      justWroteRef.current = nextSearchParams.toString() !== searchParamsRef.current.toString();
      setSearchParams(nextSearchParams, { replace: true });
    },
    [setSearchParams],
  );

  // When hydrated from localStorage onto a clean URL, write the canonical URL
  // once so the restored filters become shareable and back-button-safe.
  // `writeState` starts from the previous params, preserving non-filter keys
  // such as `view`. Declared BEFORE the sync effect and arms `justWroteRef` when
  // this changes the query, so the sync effect's first pass cannot re-read stale
  // URL defaults over the restored state. Runs exactly once (the ref is flipped).
  useEffect(() => {
    if (!hydratedFromStorageRef.current) return;
    hydratedFromStorageRef.current = false;
    const nextSearchParams = writeState(searchParamsRef.current, stateRef.current);
    justWroteRef.current = nextSearchParams.toString() !== searchParamsRef.current.toString();
    setSearchParams(nextSearchParams, { replace: true });
  }, [setSearchParams]);

  // Sync local state when the URL changes from OUTSIDE the hook — back/forward
  // navigation or external deep-link replay. INVARIANT: skip the settle that
  // came from our own write (via `justWroteRef`); otherwise compare canonical
  // surfaces and only re-read when they differ, so this never loops.
  useEffect(() => {
    if (justWroteRef.current) {
      justWroteRef.current = false;
      return;
    }
    // External navigation to a clean `/cargo` (e.g. the Cargo sidebar link)
    // lands here with no persisted filter key. Treat it like initial hydration
    // and RESTORE the saved filter instead of clobbering state to defaults — a
    // clobber would also make the write-through effect delete the persisted key
    // (#2104 BR-1/BR-4/BR-5). Our own filter clears never reach this branch
    // (they early-return via `justWroteRef`), so an intentional clear still
    // removes storage. `q` is URL-only — taken from the URL, never restored.
    if (!hasPersistedCargoFilterParam(searchParams)) {
      const stored = safeReadCargoFilters();
      if (stored != null && stored.length > 0) {
        const sp = new URLSearchParams(stored);
        sp.delete('q');
        const restored = { ...readState(sp), q: readState(searchParams).q };
        stateRef.current = restored;
        setState(restored);
        // Canonicalize once so the restored filter is shareable and non-filter
        // params (e.g. `view`) survive. `justWroteRef` makes the re-fired sync
        // effect early-return, so there is no loop.
        const nextSearchParams = writeState(searchParamsRef.current, restored);
        justWroteRef.current = nextSearchParams.toString() !== searchParamsRef.current.toString();
        setSearchParams(nextSearchParams, { replace: true });
        return;
      }
    }
    const next = readState(searchParams);
    const prevSurface = writeState(new URLSearchParams(), stateRef.current).toString();
    const nextSurface = writeState(new URLSearchParams(), next).toString();
    if (prevSurface === nextSurface) return;
    stateRef.current = next;
    setState(next);
  }, [searchParams, setSearchParams]);

  // Auth-guarded, idempotent write-through to localStorage (#2104). Mirrors
  // `useRequestsFilters`: re-check the live user immediately before every put
  // so an anonymous shared URL or a cross-tab logout race cannot reseed the
  // key after `userCacheReset` removed it. `q` is excluded by
  // `buildPersistedCargoQuery`; an empty payload removes the key.
  useEffect(() => {
    const value = buildPersistedCargoQuery(state);
    if (lastWrittenStorageRef.current === value) return;
    if (useAuthStore.getState().user === null) return;
    safeWriteCargoFilters(value);
    lastWrittenStorageRef.current = value;
  }, [state]);

  const setQ = useCallback(
    (next: string) => {
      const clamped = next.slice(0, CARGO_SEARCH_MAX_CHARS);
      commit((p) => (p.q === clamped ? p : { ...p, q: clamped }));
    },
    [commit],
  );
  const setLifecycle = useCallback(
    (next: CargoLifecycle | null) =>
      commit((p) => (p.lifecycle === next ? p : { ...p, lifecycle: next })),
    [commit],
  );
  const setResponsibleManagerId = useCallback(
    (next: string) =>
      commit((p) => (p.responsibleManagerId === next ? p : { ...p, responsibleManagerId: next })),
    [commit],
  );
  const setCarrier = useCallback(
    (next: string | null) => commit((p) => (p.carrier === next ? p : { ...p, carrier: next })),
    [commit],
  );
  const setPathFormat = useCallback(
    (next: string | null) =>
      commit((p) => (p.pathFormat === next ? p : { ...p, pathFormat: next })),
    [commit],
  );
  const setClientPaymentStatus = useCallback(
    (next: CargoClientPaymentStatus | null) =>
      commit((p) => (p.clientPaymentStatus === next ? p : { ...p, clientPaymentStatus: next })),
    [commit],
  );
  const setCarrierPaymentStatus = useCallback(
    (next: CargoCarrierPaymentStatus | null) =>
      commit((p) => (p.carrierPaymentStatus === next ? p : { ...p, carrierPaymentStatus: next })),
    [commit],
  );
  // Date setters always stamp `datesTouched: true` — any deliberate date
  // interaction (including clearing back to '') hands control to the user and
  // must suppress the logist default window from then on.
  const setDateType = useCallback(
    (next: CargoDateType) =>
      commit((p) =>
        p.dateType === next && p.datesTouched ? p : { ...p, dateType: next, datesTouched: true },
      ),
    [commit],
  );
  const setDateFrom = useCallback(
    (next: string) =>
      commit((p) =>
        p.dateFrom === next && p.datesTouched ? p : { ...p, dateFrom: next, datesTouched: true },
      ),
    [commit],
  );
  const setDateTo = useCallback(
    (next: string) =>
      commit((p) =>
        p.dateTo === next && p.datesTouched ? p : { ...p, dateTo: next, datesTouched: true },
      ),
    [commit],
  );
  const setRangeBound = useCallback(
    (key: CargoRangeBoundKey, next: string) =>
      commit((p) => (p[key] === next ? p : { ...p, [key]: next })),
    [commit],
  );
  const setOverdue = useCallback(
    (next: CargoOverdueBucket | null) =>
      commit((p) => (p.overdue === next ? p : { ...p, overdue: next })),
    [commit],
  );
  const setHasRejectedDocument = useCallback(
    (next: boolean) =>
      commit((p) => (p.hasRejectedDocument === next ? p : { ...p, hasRejectedDocument: next })),
    [commit],
  );
  const resetFilters = useCallback(
    () => commit(() => ({ ...readState(new URLSearchParams()), datesTouched: true })),
    [commit],
  );

  const hasExplicitDates = state.dateFrom.length > 0 || state.dateTo.length > 0;
  const usingDefaultDateWindow = applyDefaultDateWindow && !state.datesTouched && !hasExplicitDates;

  // Compute the default window dates from the SAME `now` used in wireFilters so
  // the chip displays exactly what is sent. Derived here (outside useMemo) so
  // it is always current at render time, matching the live Date() in wireFilters.
  const defaultDateWindow = useMemo<{ from: string; to: string } | null>(() => {
    if (!usingDefaultDateWindow) return null;
    const now = new Date();
    return {
      from: toLocalDateStr(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      to: toLocalDateStr(now),
    };
  }, [usingDefaultDateWindow]);

  const wireFilters = useMemo<Partial<CargoListQuery>>(() => {
    const out: Partial<CargoListQuery> = {};
    const q = state.q.trim().slice(0, CARGO_SEARCH_MAX_CHARS);
    if (q) out.q = q;
    if (state.lifecycle) out.lifecycle = state.lifecycle;
    if (state.responsibleManagerId) out.responsibleManagerId = state.responsibleManagerId;
    if (state.carrier) out.carrier = state.carrier;
    if (state.pathFormat) out.pathFormat = state.pathFormat;
    if (state.clientPaymentStatus) out.clientPaymentStatus = state.clientPaymentStatus;
    if (state.carrierPaymentStatus) out.carrierPaymentStatus = state.carrierPaymentStatus;
    if (state.overdue) out.overdue = state.overdue;
    if (state.hasRejectedDocument) out.hasRejectedDocument = true;
    const rangeBounds = [
      ['weightFrom', state.weightFrom],
      ['weightTo', state.weightTo],
      ['volumeFrom', state.volumeFrom],
      ['volumeTo', state.volumeTo],
      ['totalCostFrom', state.totalCostFrom],
      ['totalCostTo', state.totalCostTo],
    ] as const;
    for (const [key, value] of rangeBounds) {
      if (isCargoRangeBoundActive(value)) out[key] = Number(value);
    }

    if (hasExplicitDates) {
      out.dateType = state.dateType;
      if (state.dateFrom) out.dateFrom = dayStartIso(state.dateFrom);
      if (state.dateTo) out.dateTo = dayEndIso(state.dateTo);
    } else if (applyDefaultDateWindow && !state.datesTouched) {
      // Frontend-only recent-shipments window for logist roles — only while the
      // user has not taken control of the date filter. Once `datesTouched` is
      // set (including by clearing the dates) we send NO date filter, so the
      // logist sees an unbounded range.
      out.dateType = 'china_ship';
      out.dateFrom = firstDayOfPreviousMonthIso(new Date());
      out.dateTo = new Date().toISOString();
    }
    return out;
  }, [state, hasExplicitDates, applyDefaultDateWindow]);

  return {
    ...state,
    setQ,
    setLifecycle,
    setResponsibleManagerId,
    setCarrier,
    setPathFormat,
    setClientPaymentStatus,
    setCarrierPaymentStatus,
    setDateType,
    setDateFrom,
    setDateTo,
    setRangeBound,
    setOverdue,
    setHasRejectedDocument,
    resetFilters,
    usingDefaultDateWindow,
    defaultDateWindow,
    wireFilters,
  };
}
